import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import Footer from '../components/Footer';
import CtaStrip from '../components/CtaStrip';

const CATEGORY_COLORS = {
  'Plugin Development': { color: 'var(--ms-blue)', bg: 'var(--ms-blue-pale)' },
  'Hiring Advice':      { color: 'var(--ms-teal)', bg: '#DFF6F0' },
  'Power Platform':     { color: 'var(--ms-purple)', bg: '#F4F0FF' },
  'PCF Development':    { color: 'var(--ms-orange)', bg: '#FFF1EC' },
  'CRM Customization':  { color: 'var(--ms-blue-dark)', bg: '#EFF6FC' },
  'PVA & Chatbots':     { color: 'var(--ms-teal)', bg: '#DFF6F0' },
};

export default function BlogPage({ onNavigate }) {
  const nav = useNavigate();

  const goToPost = (slug) => {
    nav(`/blog/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <Helmet>
        <title>D365 Blog — Dynamics 365 Tips, Guides & Best Practices | AdjustD365</title>
        <meta name="description" content="Expert Dynamics 365 articles covering plugin development, Power Automate, PCF controls, PVA chatbots, and CRM customization. Written by an experienced D365 consultant." />
        <link rel="canonical" href="https://adjustd365.com/blog" />
      </Helmet>

      {/* PAGE HERO */}
      <div className="section-pad" style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '56px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '13px', color: 'var(--text-s)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', color: 'var(--text-s)', cursor: 'pointer', fontSize: '13px' }}>Home</button>
            <span>›</span>
            <span style={{ color: 'var(--ms-blue)', fontWeight: 500 }}>Blog</span>
          </div>
          <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ms-blue)', marginBottom: '10px' }}>D365 Knowledge Base</div>
          <h1 style={{ fontSize: 'clamp(26px,3vw,42px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', marginBottom: '12px' }}>
            Dynamics 365 Guides & Best Practices
          </h1>
          <p style={{ fontSize: '16px', color: 'var(--text-m)', maxWidth: '580px' }}>
            Practical articles on D365 development, customisation, and consulting — written by a developer who builds this stuff every day.
          </p>
        </div>
      </div>

      {/* BLOG GRID */}
      <section className="section-pad" style={{ padding: '80px 48px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '24px' }} className="grid-3col">
            {blogPosts.map((post) => {
              const catStyle = CATEGORY_COLORS[post.category] || { color: 'var(--ms-blue)', bg: 'var(--ms-blue-pale)' };
              return (
                <article
                  key={post.slug}
                  onClick={() => goToPost(post.slug)}
                  style={{
                    background: '#fff', border: '1px solid var(--border)', borderRadius: '6px',
                    padding: '28px', cursor: 'pointer', transition: 'all 0.2s',
                    display: 'flex', flexDirection: 'column',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,120,212,0.1)'; e.currentTarget.style.borderColor = 'var(--ms-blue)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px', color: catStyle.color, background: catStyle.bg }}>
                      {post.category}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-s)' }}>{post.readTime}</span>
                  </div>
                  <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-h)', lineHeight: 1.4, marginBottom: '12px', flexGrow: 1 }}>
                    {post.title}
                  </h2>
                  <p style={{ fontSize: '13px', color: 'var(--text-m)', lineHeight: 1.65, marginBottom: '20px' }}>
                    {post.excerpt}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-s)' }}>{post.date}</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ms-blue)' }}>Read article →</span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <CtaStrip
        heading="Have a D365 project in mind?"
        subtext="The best way to learn how to fix a D365 problem is to work with someone who's solved it before."
        buttonLabel="Get in Touch"
        onAction={() => onNavigate('contact')}
      />
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
